import React, { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { LYT_INFO } from './config/constants';

// Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import ContactPage from './pages/ContactPage';
import PortalSelect from './pages/PortalSelect';
import InviteCodePage from './pages/InviteCodePage';
import ApiTestPage from './pages/ApiTestPage';
import NDASignPage from './pages/NDASignPage';

// Portals
import EmployeeOnboarding from './portals/EmployeeOnboarding';
import ContractorOnboarding from './portals/ContractorOnboarding';
import EmployeeLogin from './portals/EmployeeLogin';
import ContractorLogin from './portals/ContractorLogin';
import EmployeeDashboard from './portals/EmployeeDashboard';
import ContractorDashboard from './portals/ContractorDashboard';
import SubDashboard from './portals/SubDashboard';
import InhouseDashboard from './portals/InhouseDashboard';
import ContractorInvoice from './portals/ContractorInvoice';
import AdminLogin from './portals/AdminLogin';
import AdminDashboard from './portals/AdminDashboard';
import PortalLogin from './portals/PortalLogin';
import SetPassword from './portals/SetPassword';
import ForgotPassword from './portals/ForgotPassword';

// New Admin & Supervisor Components
import AdminUserManagement from './portals/AdminUserManagement';
import RecruitingTracker from './portals/RecruitingTracker';
import WorkMap from './portals/WorkMap';
import PotholeVerification from './portals/PotholeVerification';

// Phase 6 - Document Generation
import DailyWorkSheet from './portals/DailyWorkSheet';
import InvoiceGenerator from './portals/InvoiceGenerator';
import DailyStreetSheet from './portals/DailyStreetSheet';
import ContractorReviewChecklist from './portals/ContractorReviewChecklist';

// Phase 7 - Advanced Admin
import MetricsDashboard from './portals/MetricsDashboard';
import SegmentReassignment from './portals/SegmentReassignment';
import AssetManagement from './portals/AssetManagement';
import ActivityLog from './portals/ActivityLog';

// Phase 8 - User Self-Service
import UserProfile from './portals/UserProfile';

// Phase 9 - Project Map System
import JobImportPage from './pages/JobImportPage';
import ProjectMapPage from './pages/ProjectMapPage';
import AdminProjectDashboard from './pages/AdminProjectDashboard';
import DailyProductionReport from './pages/DailyProductionReport';

// Phase 12 - P&L Dashboard + Client Invoicing
import PLDashboard from './pages/PLDashboard';
import ClientInvoicePage from './pages/ClientInvoicePage';

// Phase 13 - Payment Tracking
import PaymentTracker from './pages/PaymentTracker';
import PaymentInvoicePage from './pages/PaymentInvoicePage';

// Shareable Rate Card
import BastropRateCard from './pages/BastropRateCard';
import NdaRateCard from './pages/NdaRateCard';

// Legal Pages
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';

// Documentation
import UserGuidePage from './pages/UserGuidePage';

// Phase 10 - JSON Import
import JsonImportPage from './pages/JsonImportPage';

// Phase 11 - Map Tracer
import MapTracerPage from './pages/MapTracerPage';

// LYT Field App Download
import FieldAppPage from './pages/FieldAppPage';

// Shared / Public Map (GC view-only)
import SharedMapPage from './pages/SharedMapPage';

// DFW Airport Contractor Portal
import DFWPortalPage from './pages/DFWPortalPage';

// Street Sheet + Utility Hit Systems
import StreetSheetListPage from './portals/StreetSheetListPage';
import UtilityHitPage from './portals/UtilityHitPage';
import UtilityHitListPage from './portals/UtilityHitListPage';

// Field Crew Hub + Safety
import FieldHubPage from './pages/FieldHubPage';
import SafetyPage from './pages/SafetyPage';

// Phase 9 - Field Assist Chat (disabled for now — component files preserved)
// import FieldAssist from './components/Chat/FieldAssist';

// Error Boundary for catching render errors
import ErrorBoundary from './components/ErrorBoundary';

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('lyt-dark-mode');
    return saved !== null ? saved === 'true' : true;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(() => {
    try {
      const saved = localStorage.getItem('lyt-logged-in-user');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  // Chat state (Field Assist disabled for now)
  // const [chatOpen, setChatOpen] = useState(false);
  // const [chatMinimized, setChatMinimized] = useState(false);
  // const [chatHidden, setChatHidden] = useState(() => sessionStorage.getItem('lyt-chat-hidden') === 'true');
  const [selectedProjectId, setSelectedProjectId] = useState(
    () => localStorage.getItem('lyt-selected-project') || null
  );
  const [reviewContractor, setReviewContractor] = useState(null);
  const [onboardingContext, setOnboardingContext] = useState(null);

  const bgColor = darkMode ? '#0d1b2a' : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  
  // Dynamic accent colors matching the gradient extremes
  // Dark mode gradient: purple (#667eea) -> pink (#c850c0) -> orange (#ff6b35)
  // Light mode gradient: blue (#0077B6) -> teal (#00b4d8) -> green (#28a745)
  const accentPortal = darkMode ? '#667eea' : '#00b4d8';        // Purple vs Teal (Portal button)
  const accentOnboarding = darkMode ? '#ff6b35' : '#28a745';    // Orange vs Green (Onboarding button)
  const accentHighlight = darkMode ? '#ff6b35' : '#00b4d8';     // For nav highlights
  
  // Logo text colors matching actual logo images
  const logoLY = darkMode ? '#e6c4d9' : '#0a3a7d';              // Pink (dark) vs Deep blue (light)
  const logoT = darkMode ? '#e6c4d9' : '#2ec7c0';               // Pink (dark) vs Teal (light)
  const logoComm = darkMode ? '#ffffff' : '#1e293b';            // White (dark) vs Dark gray (light)

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'services', label: 'Services' },
    { id: 'contact', label: 'Contact' },
  ];

  // Persist dark mode preference
  useEffect(() => {
    localStorage.setItem('lyt-dark-mode', String(darkMode));
  }, [darkMode]);

  // Persist selected project
  useEffect(() => {
    if (selectedProjectId) localStorage.setItem('lyt-selected-project', selectedProjectId);
  }, [selectedProjectId]);

  // Persist logged-in user across refreshes
  useEffect(() => {
    if (loggedInUser) {
      localStorage.setItem('lyt-logged-in-user', JSON.stringify(loggedInUser));
    } else {
      localStorage.removeItem('lyt-logged-in-user');
    }
  }, [loggedInUser]);

  // Dynamic favicon based on theme
  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      favicon.href = darkMode ? '/lyt_logo_dark.png' : '/lyt_logo_light.png';
    }
    // Also update theme-color meta tag
    const themeColor = document.querySelector("meta[name='theme-color']");
    if (themeColor) {
      themeColor.content = darkMode ? '#0d1b2a' : '#ffffff';
    }
  }, [darkMode]);

  // Browser history integration - back button support
  useEffect(() => {
    // Handle browser back/forward buttons
    const handlePopState = (event) => {
      if (event.state && event.state.page) {
        setCurrentPage(event.state.page);
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Handle hash changes (e.g. user types #map-tracer in address bar)
    const handleHashChange = () => {
      const rawHash = window.location.hash.replace('#', '');
      const hash = rawHash.split('?')[0]; // Strip query params
      if (hash) {
        // Short URL aliases
        const aliases = { 'app': 'field-app' };
        setCurrentPage(aliases[hash] || hash);
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('hashchange', handleHashChange);

    // Check for hash in URL on initial load
    const rawHash = window.location.hash.replace('#', '');
    const hash = rawHash.split('?')[0]; // Strip query params
    if (hash) {
      const aliases = { 'app': 'field-app' };
      setCurrentPage(aliases[hash] || hash);
      window.history.replaceState({ page: hash }, '', window.location.hash);
    } else if (!window.history.state) {
      window.history.replaceState({ page: 'home' }, '', window.location.pathname);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const handleNavClick = (page) => {
    // Push new state to browser history
    window.history.pushState({ page }, '', `#${page}`);
    setCurrentPage(page);
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  // Pages that don't show the main header/footer
  const portalPages = [
    'portal',
    'portal-login',
    'onboarding',
    'employee-onboarding',
    'contractor-onboarding',
    'employee-login',
    'contractor-login',
    'employee-dashboard',
    'contractor-dashboard',
    'sub-dashboard',
    'contractor-invoice',
    'admin-login',
    'admin-dashboard',
    'admin-users',
    'recruiting',
    'work-map',
    'potholes',
    'daily-worksheet',
    'street-sheet',
    'invoices',
    'metrics',
    'activity-log',
    'profile',
    'set-password',
    'forgot-password',
    'api-test',
    'nda-sign',
    'job-import',
    'project-map',
    'admin-projects',
    'daily-report',
    'json-import',
    'contractor-review',
    'map-tracer',
    'pl-dashboard',
    'client-invoice',
    'payment-tracker',
    'payment-invoices',
    'bastrop-rates',
    'nda-rate-card',
    'field-app',
    'inhouse-dashboard',
    'shared-map',
    'dfw-portal',
    'street-sheet-list',
    'utility-hit',
    'utility-hit-list',
    'field-hub',
    'safety',
    'segment-reassign',
    'asset-management',
  ];

  const isPortalPage = portalPages.includes(currentPage);

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage setCurrentPage={handleNavClick} darkMode={darkMode} />;
      case 'about':
        return <AboutPage darkMode={darkMode} />;
      case 'services':
        return <ServicesPage setCurrentPage={handleNavClick} darkMode={darkMode} />;
      case 'contact':
        return <ContactPage darkMode={darkMode} />;
      case 'portal':
        return <PortalSelect setCurrentPage={handleNavClick} darkMode={darkMode} />;
      case 'employee-onboarding':
        return <EmployeeOnboarding setCurrentPage={handleNavClick} onboardingContext={onboardingContext} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'contractor-onboarding':
        return <ContractorOnboarding setCurrentPage={handleNavClick} onboardingContext={onboardingContext} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'employee-login':
        return <EmployeeLogin setCurrentPage={handleNavClick} setLoggedInUser={setLoggedInUser} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'contractor-login':
        return <ContractorLogin setCurrentPage={handleNavClick} setLoggedInUser={setLoggedInUser} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'employee-dashboard':
        return <EmployeeDashboard setCurrentPage={handleNavClick} loggedInUser={loggedInUser} setLoggedInUser={setLoggedInUser} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'contractor-dashboard':
        return <ContractorDashboard setCurrentPage={handleNavClick} loggedInUser={loggedInUser} setLoggedInUser={setLoggedInUser} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'sub-dashboard':
        return <SubDashboard setCurrentPage={handleNavClick} loggedInUser={loggedInUser} setLoggedInUser={setLoggedInUser} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'inhouse-dashboard':
        return <InhouseDashboard setCurrentPage={handleNavClick} loggedInUser={loggedInUser} setLoggedInUser={setLoggedInUser} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'contractor-invoice':
        return <ContractorInvoice darkMode={darkMode} user={loggedInUser} setCurrentPage={handleNavClick} />;
      case 'admin-login':
        return <AdminLogin setCurrentPage={handleNavClick} setLoggedInUser={setLoggedInUser} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'admin-dashboard':
        return <AdminDashboard setCurrentPage={handleNavClick} loggedInUser={loggedInUser} setLoggedInUser={setLoggedInUser} darkMode={darkMode} setDarkMode={setDarkMode} setReviewContractor={setReviewContractor} />;
      case 'admin-users':
        return <AdminUserManagement darkMode={darkMode} currentUser={loggedInUser} setCurrentPage={setCurrentPage} />;
      case 'recruiting':
        return <RecruitingTracker darkMode={darkMode} user={loggedInUser} setCurrentPage={setCurrentPage} />;
      case 'work-map':
        return <WorkMap darkMode={darkMode} user={loggedInUser} userType={loggedInUser?.role || 'employee'} setCurrentPage={setCurrentPage} />;
      case 'potholes':
        return <PotholeVerification darkMode={darkMode} user={loggedInUser} userType={loggedInUser?.role || 'employee'} setCurrentPage={setCurrentPage} />;
      case 'daily-worksheet':
        return <DailyWorkSheet darkMode={darkMode} user={loggedInUser} setCurrentPage={setCurrentPage} />;
      case 'street-sheet':
        return <DailyStreetSheet darkMode={darkMode} user={loggedInUser} setCurrentPage={handleNavClick} loggedInUser={loggedInUser} />;
      case 'contractor-review':
        return <ContractorReviewChecklist darkMode={darkMode} user={loggedInUser} setCurrentPage={handleNavClick} contractor={reviewContractor} loggedInUser={loggedInUser} />;
      case 'invoices':
        return <InvoiceGenerator darkMode={darkMode} user={loggedInUser} setCurrentPage={setCurrentPage} />;
      case 'metrics':
        return <MetricsDashboard darkMode={darkMode} user={loggedInUser} setCurrentPage={setCurrentPage} />;
      case 'activity-log':
        return <ActivityLog darkMode={darkMode} user={loggedInUser} setCurrentPage={setCurrentPage} />;
      case 'profile':
        return <UserProfile darkMode={darkMode} setDarkMode={setDarkMode} user={loggedInUser} setCurrentPage={handleNavClick} />;
      case 'portal-login':
        return <PortalLogin setCurrentPage={handleNavClick} setLoggedInUser={setLoggedInUser} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'onboarding':
        return <InviteCodePage setCurrentPage={handleNavClick} setOnboardingContext={setOnboardingContext} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'set-password':
        return <SetPassword setCurrentPage={handleNavClick} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'forgot-password':
        return <ForgotPassword setCurrentPage={handleNavClick} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'api-test':
        return <ApiTestPage darkMode={darkMode} setCurrentPage={setCurrentPage} />;
      case 'nda-sign':
        return <NDASignPage setCurrentPage={handleNavClick} darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'job-import':
        return <ErrorBoundary><JobImportPage darkMode={darkMode} setDarkMode={setDarkMode} user={loggedInUser} setCurrentPage={handleNavClick} /></ErrorBoundary>;
      case 'project-map':
        return <ErrorBoundary><ProjectMapPage darkMode={darkMode} setDarkMode={setDarkMode} user={loggedInUser} setCurrentPage={handleNavClick} projectId={selectedProjectId} /></ErrorBoundary>;
      case 'admin-projects':
        return <ErrorBoundary><AdminProjectDashboard darkMode={darkMode} setDarkMode={setDarkMode} user={loggedInUser} setCurrentPage={handleNavClick} selectedProjectId={selectedProjectId} setSelectedProjectId={setSelectedProjectId} /></ErrorBoundary>;
      case 'daily-report':
        return <DailyProductionReport darkMode={darkMode} setDarkMode={setDarkMode} user={loggedInUser} setCurrentPage={handleNavClick} projectId={selectedProjectId} />;
      case 'json-import':
        return <JsonImportPage darkMode={darkMode} setDarkMode={setDarkMode} user={loggedInUser} setCurrentPage={handleNavClick} />;
      case 'map-tracer':
        return <ErrorBoundary><MapTracerPage darkMode={darkMode} setCurrentPage={handleNavClick} /></ErrorBoundary>;
      case 'pl-dashboard':
        return <PLDashboard darkMode={darkMode} user={loggedInUser} setCurrentPage={handleNavClick} />;
      case 'client-invoice':
        return <ClientInvoicePage darkMode={darkMode} user={loggedInUser} setCurrentPage={handleNavClick} />;
      case 'payment-tracker':
        return <PaymentTracker darkMode={darkMode} user={loggedInUser} setCurrentPage={handleNavClick} projectId={selectedProjectId} />;
      case 'payment-invoices':
        return <PaymentInvoicePage darkMode={darkMode} user={loggedInUser} setCurrentPage={handleNavClick} projectId={selectedProjectId} />;
      case 'privacy':
        return <PrivacyPage darkMode={darkMode} />;
      case 'terms':
        return <TermsPage darkMode={darkMode} />;
      case 'user-guide':
        if (!loggedInUser) { handleNavClick('portal-login'); return null; }
        return <UserGuidePage darkMode={darkMode} setCurrentPage={handleNavClick} loggedInUser={loggedInUser} />;
      case 'segment-reassign':
        return <SegmentReassignment darkMode={darkMode} setDarkMode={setDarkMode} loggedInUser={loggedInUser} setCurrentPage={handleNavClick} />;
      case 'asset-management':
        return <AssetManagement darkMode={darkMode} setDarkMode={setDarkMode} loggedInUser={loggedInUser} setCurrentPage={handleNavClick} />;
      case 'bastrop-rates':
        return <BastropRateCard darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'nda-rate-card':
        return <NdaRateCard darkMode={darkMode} setDarkMode={setDarkMode} setCurrentPage={handleNavClick} onboardingContext={onboardingContext} />;
      case 'field-app':
        return <FieldAppPage darkMode={darkMode} setDarkMode={setDarkMode} />;
      case 'shared-map':
        return <ErrorBoundary><SharedMapPage /></ErrorBoundary>;
      case 'dfw-portal':
        return <DFWPortalPage darkMode={darkMode} setDarkMode={setDarkMode} setCurrentPage={handleNavClick} loggedInUser={loggedInUser} />;
      case 'street-sheet-list':
        return <StreetSheetListPage darkMode={darkMode} setCurrentPage={handleNavClick} loggedInUser={loggedInUser} />;
      case 'utility-hit':
        return <UtilityHitPage darkMode={darkMode} setCurrentPage={handleNavClick} loggedInUser={loggedInUser} />;
      case 'utility-hit-list':
        return <UtilityHitListPage darkMode={darkMode} setCurrentPage={handleNavClick} loggedInUser={loggedInUser} />;
      case 'field-hub':
        return <FieldHubPage darkMode={darkMode} setCurrentPage={handleNavClick} />;
      case 'safety':
        return <SafetyPage darkMode={darkMode} setCurrentPage={handleNavClick} />;
      default:
        return <HomePage setCurrentPage={handleNavClick} darkMode={darkMode} />;
    }
  };

  // For portal pages, render without header/footer
  if (isPortalPage) {
    return (
      <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        {renderPage()}
      </div>
    );
  }

  // Main website with header/footer
  return (
    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", backgroundColor: bgColor, color: textColor, minHeight: '100vh' }}>
      {/* Top Bar - Minimal */}
      <div style={{ 
        backgroundColor: darkMode ? '#112240' : '#f1f5f9', 
        padding: '6px 20px', 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center', 
        fontSize: '0.85rem' 
      }}>
        <button
          onClick={() => setDarkMode(!darkMode)}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: darkMode ? 'rgba(255,255,255,0.8)' : '#64748b',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '0.85rem',
          }}
        >
          {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          <span className="hide-mobile">{darkMode ? 'Light' : 'Dark'} Mode</span>
        </button>
      </div>

      {/* Header */}
      <header style={{ 
        backgroundColor: darkMode ? '#0d1b2a' : '#ffffff', 
        padding: '16px 20px', 
        position: 'sticky', 
        top: 0, 
        zIndex: 1000,
        borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Logo */}
          <button
            onClick={() => handleNavClick('home')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
              <span style={{ color: logoLY }}>ly</span>
              <span style={{ color: logoT }}>t</span>
              <span style={{ fontWeight: '400', fontSize: '1.25rem', marginLeft: '4px', color: logoComm }}>Communications</span>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '4px' }}>
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => handleNavClick(link.id)}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: currentPage === link.id ? `${accentHighlight}20` : 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: currentPage === link.id ? accentHighlight : textColor,
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseOver={(e) => {
                    if (currentPage !== link.id) e.currentTarget.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
                  }}
                  onMouseOut={(e) => {
                    if (currentPage !== link.id) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {link.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => handleNavClick('portal-login')}
              style={{
                padding: '10px 20px',
                backgroundColor: accentPortal,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginLeft: '12px',
              }}
            >
              Portal
            </button>
            <button
              onClick={() => handleNavClick('onboarding')}
              style={{
                padding: '10px 20px',
                backgroundColor: accentOnboarding,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginLeft: '8px',
              }}
            >
              Onboarding
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: textColor,
              cursor: 'pointer',
              padding: '8px',
            }}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu" style={{ 
            padding: '20px', 
            backgroundColor: darkMode ? '#112240' : '#f8fafc', 
            marginTop: '16px', 
            borderRadius: '12px' 
          }}>
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => handleNavClick(link.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '14px 16px',
                  backgroundColor: currentPage === link.id ? `${accentHighlight}20` : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: currentPage === link.id ? accentHighlight : textColor,
                  fontSize: '1rem',
                  textAlign: 'left',
                  cursor: 'pointer',
                  marginBottom: '4px',
                }}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => handleNavClick('portal-login')}
              style={{
                display: 'block',
                width: '100%',
                padding: '14px 16px',
                backgroundColor: accentPortal,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '12px',
              }}
            >
              Portal
            </button>
            <button
              onClick={() => handleNavClick('onboarding')}
              style={{
                display: 'block',
                width: '100%',
                padding: '14px 16px',
                backgroundColor: accentOnboarding,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '8px',
              }}
            >
              Onboarding
            </button>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>{renderPage()}</main>

      {/* Footer - Clean & Minimal */}
      <footer style={{ 
        backgroundColor: darkMode ? '#0a1628' : '#f1f5f9', 
        padding: '48px 20px 24px', 
        color: darkMode ? 'rgba(255,255,255,0.7)' : '#64748b' 
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Main Footer Content */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '32px',
            marginBottom: '32px',
          }}>
            {/* Logo & Tagline */}
            <div style={{ flex: '1 1 280px' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '12px' }}>
                <span style={{ color: logoLY }}>ly</span>
                <span style={{ color: logoT }}>t</span>
                <span style={{ fontWeight: '400', fontSize: '1.1rem', marginLeft: '4px', color: logoComm }}>Communications</span>
              </div>
              <p style={{ fontSize: '0.9rem', lineHeight: '1.6', maxWidth: '280px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#64748b' }}>
                Professional fiber optic construction across the Gulf Coast.
              </p>
              <p style={{ fontSize: '0.75rem', marginTop: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#94a3b8', lineHeight: '1.5' }}>
                Texas • Louisiana • Mississippi<br />Florida • Alabama
              </p>
            </div>

            {/* Navigation */}
            <div style={{ display: 'flex', gap: '48px', flexWrap: 'wrap' }}>
              <div>
                <h4 style={{ color: darkMode ? '#fff' : '#1e293b', fontWeight: '600', marginBottom: '12px', fontSize: '0.9rem' }}>Navigate</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {navLinks.map((link) => (
                    <button
                      key={link.id}
                      onClick={() => handleNavClick(link.id)}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: darkMode ? 'rgba(255,255,255,0.7)' : '#64748b', 
                        cursor: 'pointer', 
                        textAlign: 'left', 
                        padding: 0,
                        fontSize: '0.85rem',
                        transition: 'color 0.2s',
                      }}
                      onMouseOver={(e) => e.currentTarget.style.color = accentHighlight}
                      onMouseOut={(e) => e.currentTarget.style.color = darkMode ? 'rgba(255,255,255,0.7)' : '#64748b'}
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h4 style={{ color: darkMode ? '#fff' : '#1e293b', fontWeight: '600', marginBottom: '12px', fontSize: '0.9rem' }}>Services</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.85rem' }}>
                  <span>HDD Drilling</span>
                  <span>Fiber Splicing</span>
                  <span>Aerial Construction</span>
                  <span>Underground</span>
                </div>
              </div>

              <div>
                <h4 style={{ color: darkMode ? '#fff' : '#1e293b', fontWeight: '600', marginBottom: '12px', fontSize: '0.9rem' }}>Team</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <button
                    onClick={() => handleNavClick('portal-login')}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: darkMode ? 'rgba(255,255,255,0.7)' : '#64748b', 
                      cursor: 'pointer', 
                      textAlign: 'left', 
                      padding: 0,
                      fontSize: '0.85rem',
                    }}
                  >
                    Team Portal
                  </button>
                  <button
                    onClick={() => handleNavClick('onboarding')}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: darkMode ? 'rgba(255,255,255,0.7)' : '#64748b', 
                      cursor: 'pointer', 
                      textAlign: 'left', 
                      padding: 0,
                      fontSize: '0.85rem',
                    }}
                  >
                    Onboarding
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div style={{ 
            borderTop: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)', 
            paddingTop: '20px', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            flexWrap: 'wrap', 
            gap: '12px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <p style={{ fontSize: '0.8rem', color: darkMode ? 'rgba(255,255,255,0.5)' : '#94a3b8', margin: 0 }}>
                © {new Date().getFullYear()} {LYT_INFO.name}
              </p>
              <button onClick={() => handleNavClick('privacy')} style={{ background: 'none', border: 'none', color: darkMode ? 'rgba(255,255,255,0.5)' : '#94a3b8', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}>Privacy Policy</button>
              <button onClick={() => handleNavClick('terms')} style={{ background: 'none', border: 'none', color: darkMode ? 'rgba(255,255,255,0.5)' : '#94a3b8', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}>Terms</button>
            </div>
            <button
              onClick={() => handleNavClick('contact')}
              style={{
                padding: '8px 20px',
                background: darkMode
                  ? `linear-gradient(135deg, #667eea 0%, #ff6b35 100%)`
                  : `linear-gradient(135deg, #00b4d8 0%, #28a745 100%)`,
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '500',
              }}
            >
              Get in Touch
            </button>
          </div>
        </div>
      </footer>

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: block !important; }
          .hide-mobile { display: none !important; }
        }
        @media (min-width: 769px) {
          .desktop-nav { display: flex !important; }
          .mobile-menu-btn { display: none !important; }
          .mobile-menu { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default App;
